// src/pages/DraftRecapPage.jsx
// Shows full draft recap once the season has started.
import React, { useMemo, useState } from "react";
import { auth, db } from "../firebase";
import { ref, get, update } from "firebase/database";
import { IPL_TEAMS } from "../players";

const ROLE_ORDER = ["WK", "BAT", "ALR", "BOWL"];

function generateRegularSeasonSchedule(teamNames, weeksRegular) {
  let list = teamNames.length % 2 !== 0 ? [...teamNames, "BYE"] : [...teamNames];
  const n = list.length;
  const rounds = n - 1;
  const fixed = list[0];
  let rotating = list.slice(1);
  const baseRounds = [];
  for (let r = 0; r < rounds; r++) {
    const left = [fixed, ...rotating.slice(0, n / 2 - 1)];
    const right = rotating.slice(n / 2 - 1).slice().reverse();
    const matchups = [];
    for (let i = 0; i < n / 2; i++) {
      const a = left[i], b = right[i];
      if (a === "BYE" || b === "BYE") continue;
      matchups.push({ home: r % 2 === 0 ? a : b, away: r % 2 === 0 ? b : a });
    }
    baseRounds.push(matchups);
    rotating = [rotating[rotating.length - 1], ...rotating.slice(0, rotating.length - 1)];
  }
  if (baseRounds.length >= weeksRegular) return baseRounds.slice(0, weeksRegular).map((m, i) => ({ week: i + 1, matchups: m }));
  const out = [];
  for (let w = 0; w < weeksRegular; w++) {
    const src = baseRounds[w % baseRounds.length];
    const repeatIndex = Math.floor(w / baseRounds.length);
    const matchups = repeatIndex % 2 === 0 ? src : src.map(({ home, away }) => ({ home: away, away: home }));
    out.push({ week: w + 1, matchups });
  }
  return out;
}

export function DraftRecapPage({ leagueCode, room, league }) {
  const uid = auth.currentUser?.uid;
  const [viewMode, setViewMode] = useState("board");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startStatus, setStartStatus] = useState("");

  const isHost = league?.ownerUid === uid || room?.hostId === uid;
  const seasonStarted = !!league?.season?.schedule;

  const startSeason = async () => {
    if (!leagueCode || !isHost) return;
    setStarting(true);
    setStartStatus("Generating schedule…");
    const code = leagueCode.toUpperCase();
    try {
      const [lgSnap, roomSnap] = await Promise.all([
        get(ref(db, `leagues/${code}`)),
        get(ref(db, `rooms/${code}`)),
      ]);
      if (!lgSnap.exists() || !roomSnap.exists()) { setStartStatus("League or room not found."); return; }
      const lg = lgSnap.val();
      const rm = roomSnap.val();
      const tc = rm?.settings?.teamCount ?? lg?.settings?.teamCount ?? 6;
      const teamList = (rm?.teams || []).slice(0, tc);
      const players = rm?.players || {};
      const slots = rm?.slots || {};

      const rostersByTeam = {};
      for (const t of teamList) {
        rostersByTeam[t.name] = (t.squadIds || []).map(id => {
          const p = players[String(id)];
          return p ? { id: p.id, name: p.name, role: p.role } : { id, name: `#${id}`, role: "UNK" };
        });
      }
      const ownersByTeam = {};
      Object.keys(slots).forEach(slotId => { ownersByTeam[slotId] = slots[slotId]?.claimedBy || ""; });

      const lineupsWeek1 = {};
      teamList.forEach(t => {
        lineupsWeek1[t.name] = { starters: [], bench: (t.squadIds || []).slice(0), startersBySlot: {}, locked: false, lockedAt: 0, lastSavedAt: 0 };
      });

      const regular = generateRegularSeasonSchedule(teamList.map(t => t.name), 12);
      const playoffs = tc >= 4
        ? { 13: { type: "semifinals", matchups: [{ id: "SF1", homeSeed: 1, awaySeed: 4 }, { id: "SF2", homeSeed: 2, awaySeed: 3 }] }, 14: { type: "final", matchups: [{ id: "F1", homeFrom: "SF1", awayFrom: "SF2" }] } }
        : { 14: { type: "final", matchups: [{ id: "F1", homeSeed: 1, awaySeed: 2 }] } };

      const updatesObj = {
        [`leagues/${code}/phase`]: "season",
        [`leagues/${code}/season/week`]: 1,
        [`leagues/${code}/season/teamCount`]: tc,
        [`leagues/${code}/season/ownersByTeam`]: ownersByTeam,
        [`leagues/${code}/season/rostersByTeam`]: rostersByTeam,
        [`leagues/${code}/season/lineups/1`]: lineupsWeek1,
        [`leagues/${code}/season/finalizedAt`]: Date.now(),
        [`leagues/${code}/season/config/weeksRegular`]: 12,
        [`leagues/${code}/season/config/weekSemis`]: 13,
        [`leagues/${code}/season/config/weekFinal`]: 14,
        [`leagues/${code}/season/config/playoffTeams`]: tc >= 4 ? 4 : 2,
      };
      regular.forEach(w => { updatesObj[`leagues/${code}/season/schedule/${w.week}`] = { type: "regular", matchups: w.matchups }; });
      Object.keys(playoffs).forEach(wk => { updatesObj[`leagues/${code}/season/schedule/${wk}`] = playoffs[wk]; });
      await update(ref(db), updatesObj);
      setStartStatus("✓ Season started! Head to Matchup.");
    } catch (e) {
      setStartStatus("Failed: " + e.message);
    } finally {
      setStarting(false);
    }
  };

  const picks = useMemo(() => {
    const arr = [...(room?.picks || [])];
    arr.sort((a, b) => (a.overall ?? 0) - (b.overall ?? 0));
    return arr;
  }, [room?.picks]);

  const teamCustom = useMemo(() => room?.teamCustom || {}, [room?.teamCustom]);
  const draftOrder = useMemo(() => room?.settings?.draftOrder || [], [room?.settings?.draftOrder]);
  const teams = useMemo(() => {
    const allTeams = room?.teams || [];
    if (!draftOrder.length) return allTeams;
    return [...allTeams].sort((a, b) => {
      const ai = draftOrder.indexOf(a.name);
      const bi = draftOrder.indexOf(b.name);
      return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
    });
  }, [room?.teams, draftOrder]);
  const playerMap = useMemo(() => room?.players || {}, [room?.players]);
  const slots = useMemo(() => room?.slots || {}, [room?.slots]);

  const slotLabel = (slotId) => {
    const n = teamCustom[slotId]?.name || slots[slotId]?.name;
    return (typeof n === "string" && n.trim()) ? n.trim() : slotId;
  };

  const teamRosters = useMemo(() => {
    return teams.map(t => ({
      name: t.name,
      label: slotLabel(t.name),
      players: (t.squadIds || []).map(id => playerMap[String(id)]).filter(Boolean),
    }));
  }, [teams, playerMap, slots]); // eslint-disable-line

  const activeTeam = selectedTeam ?? teams[0]?.name;

  // Draft board: rounds x teams
  const teamCount = teams.length || 1;
  const rounds = useMemo(() => {
    const map = {};
    for (const pk of picks) {
      if (!map[pk.round]) map[pk.round] = {};
      map[pk.round][pk.team] = pk;
    }
    return map;
  }, [picks]);

  const roundNums = Object.keys(rounds).map(Number).sort((a, b) => a - b);

  return (
    <div>
      {/* Season start banner — only shown if draft done but season not yet started */}
      {!seasonStarted && isHost && (
        <div style={{
          padding: "16px 20px", marginBottom: 12,
          background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.3)",
          borderRadius: "var(--radius)", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ fontWeight: 700, color: "var(--gold)", marginBottom: 3 }}>
              🏆 Draft complete — start the season!
            </div>
            <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
              Generates matchup schedule for all {room?.teams?.length || "—"} teams.
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {startStatus && (
              <span style={{ fontSize: 13, color: startStatus.startsWith("✓") ? "var(--green)" : "var(--red)" }}>
                {startStatus}
              </span>
            )}
            <button className="primary" onClick={startSeason} disabled={starting}>
              {starting ? "Starting…" : "Start Season →"}
            </button>
          </div>
        </div>
      )}
      {!seasonStarted && !isHost && (
        <div style={{
          padding: "14px 16px", marginBottom: 12,
          background: "rgba(245,166,35,0.06)", border: "1px solid rgba(245,166,35,0.2)",
          borderRadius: "var(--radius)", fontSize: 13, color: "var(--text-dim)",
        }}>
          ⏳ Waiting for the host to start the season…
        </div>
      )}

      {/* Header */}
      <div className="panel" style={{ marginBottom: 12, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Draft Recap
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
              {picks.length} picks · {teamCount} teams · {roundNums.length} rounds
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className={viewMode === "board" ? "primary" : ""}
              onClick={() => setViewMode("board")}
            >Draft Board</button>
            <button
              className={viewMode === "team" ? "primary" : ""}
              onClick={() => setViewMode("team")}
            >By Team</button>
          </div>
        </div>
      </div>

      {picks.length === 0 && (
        <div className="panel" style={{ padding: 24, textAlign: "center", color: "var(--text-dim)" }}>
          No picks recorded yet.
        </div>
      )}

      {/* ── DRAFT BOARD VIEW ── */}
      {viewMode === "board" && picks.length > 0 && (
        <div className="panel" style={{ overflowX: "auto" }}>
          <table style={{ minWidth: teamCount * 120 + 60 }}>
            <thead>
              <tr>
                <th style={{ width: 50 }}>Rnd</th>
                {teams.map(t => (
                  <th key={t.name} style={{ textAlign: "center", minWidth: 120 }}>
                    {slotLabel(t.name)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roundNums.map(rnd => (
                <tr key={rnd}>
                  <td style={{ textAlign: "center", color: "var(--text-dim)", fontWeight: 700 }}>{rnd}</td>
                  {teams.map(t => {
                    const pk = rounds[rnd]?.[t.name];
                    const pl = pk ? playerMap[String(pk.playerId)] : null;
                    const teamColor = pl ? (IPL_TEAMS[pl.team]?.color || "var(--text-dim)") : null;
                    return (
                      <td key={t.name} style={{ padding: "7px 10px", textAlign: "center" }}>
                        {pl ? (
                          <div>
                            <div style={{
                              fontSize: 12, fontWeight: 600,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              maxWidth: 110,
                            }}>{pl.name}</div>
                            <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 3 }}>
                              <span className={`role-chip role-${pl.role}`}>{pl.role}</span>
                              <span className="team-chip" style={{ color: teamColor }}>{pl.team}</span>
                            </div>
                            {pk.auto && <span style={{ fontSize: 9, color: "var(--text-muted)" }}>AUTO</span>}
                          </div>
                        ) : (
                          <span style={{ color: "var(--border2)", fontSize: 11 }}>—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── BY TEAM VIEW ── */}
      {viewMode === "team" && (
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 12 }}>
          {/* Team list */}
          <div className="panel" style={{ padding: 0, alignSelf: "start" }}>
            {teamRosters.map(t => (
              <button
                key={t.name}
                onClick={() => setSelectedTeam(t.name)}
                style={{
                  width: "100%", textAlign: "left", padding: "11px 14px",
                  background: activeTeam === t.name ? "rgba(245,166,35,0.1)" : "transparent",
                  border: "none",
                  borderBottom: "1px solid var(--border)",
                  borderLeft: activeTeam === t.name ? "3px solid var(--gold)" : "3px solid transparent",
                  color: activeTeam === t.name ? "var(--gold)" : "var(--text)",
                  fontWeight: activeTeam === t.name ? 700 : 500,
                  fontSize: 13,
                  cursor: "pointer",
                  borderRadius: 0,
                }}
              >
                {t.label}
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  {t.players.length} players
                </div>
              </button>
            ))}
          </div>

          {/* Roster */}
          <div className="panel">
            {(() => {
              const t = teamRosters.find(x => x.name === activeTeam);
              if (!t) return null;
              const grouped = {};
              for (const role of ROLE_ORDER) grouped[role] = [];
              for (const p of t.players) {
                if (!grouped[p.role]) grouped[p.role] = [];
                grouped[p.role].push(p);
              }
              return (
                <>
                  <div className="panel-head">
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{t.label}</div>
                    <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{t.players.length} players</div>
                  </div>
                  {ROLE_ORDER.map(role => {
                    const ps = grouped[role] || [];
                    if (!ps.length) return null;
                    return (
                      <div key={role}>
                        <div style={{
                          padding: "6px 14px", fontSize: 10, fontWeight: 700,
                          letterSpacing: "0.1em", color: "var(--text-muted)",
                          background: "var(--bg2)", borderBottom: "1px solid var(--border)",
                          textTransform: "uppercase",
                        }}>
                          {role} ({ps.length})
                        </div>
                        {ps.map(p => {
                          const pickInfo = picks.find(pk => String(pk.playerId) === String(p.id));
                          const teamColor = IPL_TEAMS[p.team]?.color || "var(--text-dim)";
                          return (
                            <div key={p.id} style={{
                              padding: "9px 14px", borderBottom: "1px solid var(--border)",
                              display: "flex", alignItems: "center", gap: 10,
                            }}>
                              {pickInfo && (
                                <div style={{
                                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                  background: "var(--bg2)", border: "1px solid var(--border)",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 10, fontWeight: 700, color: "var(--text-dim)",
                                }}>
                                  {pickInfo.overall}
                                </div>
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                                <div style={{ display: "flex", gap: 5, marginTop: 2 }}>
                                  <span className={`role-chip role-${p.role}`}>{p.role}</span>
                                  <span className="team-chip" style={{ color: teamColor }}>{p.team}</span>
                                </div>
                              </div>
                              {pickInfo?.auto && (
                                <span style={{ fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.06em" }}>AUTO</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
